import { Button } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import { Form } from 'components/organisms';
import { clientAccountInitialValues } from 'components/organisms/forms/client-account/ClientAccountForm';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import {
  PiArrowsClockwise,
  PiNavigationArrow,
  PiPencilSimpleLine,
} from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useDeleteClientAccount,
  useGetClientAccount,
  useSyncClientAccount,
} from 'services/client-account';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import {
  onErrorShowAllErrorsToast,
  onErrorShowToast,
} from 'utils/service/errorHandler';

const ClientAccount = () => {
  const accountId = useParamId('client-account');
  const account = useGetClientAccount(accountId);
  const navigate = useNavigate();
  const i18nextPrefix = 'user';
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const navigateToNewWindow = useNewWindowNavigate();

  const deleteAccount = useDeleteClientAccount();
  const syncAccount = useSyncClientAccount();

  const onRemoveAccount = async (actions: PopupActions) => {
    await deleteAccount.mutateAsync(accountId, {
      onError: (error: Error) => {
        actions.close();
        onErrorShowToast(error);
      },
      onSuccess: () => {
        navigate('/client-account');
      },
    });
  };

  return (
    <Form.ClientAccount
      i18nextPrefix={i18nextPrefix}
      tittle={account.data?.name ?? '-'}
      initialValues={
        account.data
          ? middleware.clientAccount.queryToForm(account.data)
          : clientAccountInitialValues
      }
      backPath='client-account'
      onSubmit={() => {}}
      status={{ displayMode: true, isLoading: account.isLoading }}
      topbarContent={
        <div className='flex  justify-end gap-2'>
          <Button
            type='button'
            variant={BUTTON_VARIANT.default}
            disabled={syncAccount.isPending || !account.data}
            isLoading={syncAccount.isPending}
            onClick={() =>
              syncAccount.mutate(
                { accountId },
                {
                  onError: error => onErrorShowAllErrorsToast(error),
                  onSuccess: () => account.refetch(),
                },
              )
            }>
            <Button.Text text={t('button.refresh')} />
            <Button.Description description={t('button.refresh')} />


            <Button.Icon icon={<PiArrowsClockwise transform='scale(-1,1)' />} />
          </Button>
          {!!account.data?.label && (
            <Button
              type='button'
              isClear
              onlyIcon
              onClick={() =>
                navigateToNewWindow(`label/${account.data?.label?.id}/finances`)
              }>
              <Button.Description description={t('button.open')} />

              <Button.Icon
                icon={<PiNavigationArrow transform='scale(-1,1)' />}
              />
            </Button>
          )}
          <Button
            isClear
            onlyIcon
            onClick={() => {
              navigate(
                `/client-account/edit/${accountId}/${getLastPathname(
                  pathname,
                )}`,
                {
                  replace: true,
                },
              );
            }}>
            <Button.Description description={t('button.edit')} />

            <Button.Icon icon={<PiPencilSimpleLine />} />
          </Button>
          <MagellanPopups.DeleteWarning
            name={account.data?.name}
            onRemove={onRemoveAccount}
          />
        </div>
      }
    />
  );
};

export default ClientAccount;
